import { type ReactNode, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Chip from '@/design_system/Chip';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconCheck from '@/icons/Check.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import { useEntity, useUpdateEntitySubLocationOptions } from '@/models/entity';

export const SubLocationOptions = ({ entityId }: { entityId: string }) => {
  const { data: entity } = useEntity(entityId);
  const {
    mutateAsync: updateSubLocationOptions,
    isPending: isUpdateEntitySubLocationOptionsPending,
  } = useUpdateEntitySubLocationOptions(entityId);

  const [isEditing, setIsEditing] = useState(false);
  const [options, setOptions] = useState<string[]>(entity?.placeConfig?.subLocationOptions ?? []);
  const [newOption, setNewOption] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!entity) {
    return null;
  }

  const handleSave = async () => {
    await updateSubLocationOptions({ subLocationOptions: options });
    setIsEditing(false);
    setNewOption('');
    setError(null);
  };

  const handleCancel = () => {
    setOptions(entity.placeConfig?.subLocationOptions ?? []);
    setNewOption('');
    setError(null);
    setIsEditing(false);
  };

  const handleChangeNewOption = (value: string) => {
    setNewOption(value);
    if (error) {
      setError(null);
    }
  };

  const handleAddOption = () => {
    const trimmed = newOption.trim();

    if (!trimmed) {
      return;
    }

    if (options.includes(trimmed)) {
      setError(
        t({
          id: 'settings.entity.place.sub-location-options.errors.duplicate',
          message: 'This sub-location already exists',
        })
      );

      return;
    }

    setOptions([...options, trimmed]);
    setNewOption('');
    setError(null);
  };

  const handleRemoveOption = (option: string) => {
    setOptions(options.filter((o) => o !== option));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  return (
    <Stack gap="1rem" style={{ marginTop: '1rem' }}>
      <Title as="h3" size="300" weight="medium">
        <Stack row gap="1rem" justifyContent="space-between" alignItems="center">
          <Trans id="settings.entity.place.sub-location-options.title">Sub-location options</Trans>
          <Stack row gap="0.5rem">
            {isEditing ? (
              <>
                <IconActionButton
                  label={t({
                    id: 'settings.entity.place.sub-location-options.cancel.label',
                    message: 'Cancel',
                  })}
                  icon={<IconCross />}
                  variant="secondary"
                  onClick={handleCancel}
                  isDisabled={isUpdateEntitySubLocationOptionsPending}
                />
                <IconActionButton
                  label={t({
                    id: 'settings.entity.place.sub-location-options.save.label',
                    message: 'Save sub-location options',
                  })}
                  icon={<IconCheck />}
                  variant="primary"
                  onClick={handleSave}
                  isLoading={isUpdateEntitySubLocationOptionsPending}
                />
              </>
            ) : (
              <IconActionButton
                label={t({
                  id: 'settings.entity.place.sub-location-options.edit.label',
                  message: 'Edit sub-location options',
                })}
                icon={<IconEdit />}
                variant="secondary"
                onClick={() => setIsEditing(true)}
              />
            )}
          </Stack>
        </Stack>
      </Title>

      <Box>
        <Stack gap="0.75rem">
          {options.length > 0 && (
            <Stack row gap="0.5rem" flexWrap="wrap">
              {options.map((option) => (
                <Chip
                  key={option}
                  size="small"
                  onCancel={isEditing ? () => handleRemoveOption(option) : undefined}
                  isDisabled={isUpdateEntitySubLocationOptionsPending}
                >
                  {option}
                </Chip>
              ))}
            </Stack>
          )}

          {isEditing && (
            <Stack row gap="0.5rem" alignItems="flex-start">
              <InputText
                value={newOption}
                onChange={handleChangeNewOption}
                onKeyDown={handleKeyDown}
                isDisabled={isUpdateEntitySubLocationOptionsPending}
                error={error ?? undefined}
                size="small"
                style={{ flex: 1 }}
                placeholder={t({
                  id: 'settings.entity.place.sub-location-options.input.placeholder',
                  message: 'Sub-location name',
                })}
              />
              <Button
                size="small"
                variant="secondary"
                onPress={handleAddOption}
                disabled={!newOption.trim() || isUpdateEntitySubLocationOptionsPending}
              >
                <Trans id="settings.entity.place.sub-location-options.add">Add</Trans>
              </Button>
            </Stack>
          )}

          {!isEditing && options.length === 0 && (
            <Trans id="settings.entity.place.sub-location-options.empty">
              No sub-location options configured.
            </Trans>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

const IconActionButton = ({
  label,
  icon,
  variant,
  onClick,
  isLoading,
  isDisabled,
}: {
  label: string;
  icon: ReactNode;
  variant: 'primary' | 'secondary';
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}) => {
  return (
    <Button
      ariaLabel={label}
      tooltip={label}
      size="medium"
      variant={variant}
      iconOnly
      onPress={onClick}
      isLoading={isLoading}
      disabled={isDisabled}
    >
      {icon}
    </Button>
  );
};
